
  import { Component } from 'vue-property-decorator'
  import axios from 'axios'
  import { GForm } from './forms/GForm'
  import { sendError } from '@/utils/notificationError'
  import { updateJWT } from '@/store/resources/form/actions'

  const ALLOWED_EXTENSIONS = new Set([
    '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', // Imágenes
    '.mp4', '.mov', '.avi', '.wmv', '.flv', '.mkv', '.webm', // Videos
    '.pdf', // PDF
    '.doc', '.docx', // Word
    '.xls', '.xlsx', // Excel
  ])

  interface TicketType {
    text: string
    value: string
    emoji: string
  }

  const { VUE_APP_LIST_ID, VUE_APP_BASE_URL } = process.env

@Component
  export default class GenerateTicket extends GForm {
  private dialog = false
  private showTaskIdDialog = false
  private showFileErrorDialog = false
  private currentTaskId = ''
  private invalidFiles: {file: string, reason: string}[] = []
  private showFileAdvice = false
  private formData = {
    type: '',
    title: '',
    comment: '',
    files: [] as File[],
  }

  private rules = {
    type: [(v: string) => !!v || 'El tipo es requerido'],
    comment: [(v: string) => !!v || 'El comentario es requerido'],
    files: [
      (v: File[]) => v.length > 0 || 'Debe adjuntar al menos un archivo',
      (v: File[]) => v.every(file => file.size > 0) || 'Uno o más archivos están vacíos, por favor vuelva a cargarlos',
    ],
  }

  private accept = Array.from(ALLOWED_EXTENSIONS).join(',')

  private loading = false

  private ticketTypes: TicketType[] = [
    { text: 'Fallo', value: 'fallo', emoji: '🐛' },
    { text: 'Mejora', value: 'mejora', emoji: '✨' },
  ]

  mounted () {
    this.$watch('formData.files', this.validateAndFilterFiles, { deep: true })

    // Agregar listeners para eventos del tour
    document.addEventListener('open-ticket-dialog', this.openDialog)
    document.addEventListener('close-ticket-dialog', () => {
      this.dialog = false
    })
  }

  beforeDestroy () {
    // Eliminar los listeners al destruir el componente para evitar memory leaks
    document.removeEventListener('open-ticket-dialog', this.openDialog)
    document.removeEventListener('close-ticket-dialog', () => {
      this.dialog = false
    })
  }

  private resetForm () {
    this.formData = {
      type: '',
      title: '',
      comment: '',
      files: [],
    }
    if (this.$refs.form) {
      (this.$refs.form as any).resetValidation()
    }
  }

  private validateAndFilterFiles (files: File[]) {
    if (!files || !files.length) return

    const validFiles: File[] = []
    this.invalidFiles = []
    this.showFileAdvice = false

    files.forEach(file => {
      if (!file.name) {
        this.invalidFiles.push({ file: 'Archivo sin nombre', reason: 'Archivo corrupto o inválido' })
        return
      }

      if (file.size === 0) {
        this.invalidFiles.push({ file: file.name, reason: 'Archivo vacío' })
        return
      }

      // Validar solo extensiones permitidas
      const extension = '.' + file.name.split('.').pop()?.toLowerCase()
      if (!ALLOWED_EXTENSIONS.has(extension)) {
        this.invalidFiles.push({ file: file.name, reason: `Extensión ${extension} no permitida` })
        this.showFileAdvice = true
        return
      }

      validFiles.push(file)
    })

    // Si se encontraron archivos inválidos
    if (this.invalidFiles.length > 0) {
      // Mostrar el diálogo de error en lugar de notificaciones
      this.showFileErrorDialog = true

      // Actualizar los archivos del formulario con solo los válidos
      this.formData.files = validFiles
    }
  }

  private getTicketEmoji (): string {
    const selectedType = this.ticketTypes.find(type => type.value === this.formData.type)
    return selectedType ? `${selectedType.emoji} ${selectedType.text}` : ''
  }

  validateFiles (files: File[]): boolean | string {
    if (!files) return true
    if (!files.length) return true

    for (const file of files) {
      if (!file.name) return 'Archivo inválido o corrupto, por favor vuelva a cargarlo'
      if (file.size === 0) return 'Archivo vacío, por favor vuelva a cargarlo'

      // Validar solo extensiones permitidas
      const extension = '.' + file.name.split('.').pop()?.toLowerCase()
      if (!ALLOWED_EXTENSIONS.has(extension)) {
        return `La extensión ${extension} no está permitida`
      }
    }
    return true
  }

  private prepareFormData (fullUrl: string): { formData: FormData; preparedData: any } {
    const formData = new FormData()
    const titleWithEmoji = `${this.getTicketEmoji()} ${this.formData.title}`

    formData.append('title', titleWithEmoji + ' ' + fullUrl + ' ' + (this as any).user.email)
    formData.append('comment', this.formData.comment)
    formData.append('url', fullUrl)
    formData.append('email', (this as any).user.email)
    formData.append('list_id', VUE_APP_LIST_ID)

    // Filtrar los archivos para incluir solo los permitidos
    const validFiles = this.formData.files.filter(file => {
      const extension = '.' + file.name.split('.').pop()?.toLowerCase()
      return ALLOWED_EXTENSIONS.has(extension)
    })

    // Si hubo archivos excluidos, mostrar una notificación
    if (validFiles.length < this.formData.files.length) {
      const excludedCount = this.formData.files.length - validFiles.length
      this.loadNotification({
        message: `Se excluyeron ${excludedCount} archivo(s) con extensiones no permitidas`,
        notificationType: 'warning',
      })
    }

    // Solo añadir archivos válidos al FormData
    validFiles.forEach(file => {
      formData.append('files', file)
    })

    const preparedData = {
      title: titleWithEmoji,
      comment: this.formData.comment,
      url: fullUrl,
      email: (this as any).user.email,
      list_id: VUE_APP_LIST_ID,
      files: validFiles, // Usar solo los archivos válidos
    }

    return { formData, preparedData }
  }

  private handleError (error: any, data: any) {
    const context = {
      app_name: 'GENIO',
      view: this.$router.currentRoute.path,
      record_id: `generate-ticket: ${JSON.stringify(data)}`,
      context: `${JSON.stringify(error)} error generando el ticket`,
    }
    sendError(context)

    // Mostrar mensaje de error más específico al usuario
    let errorMessage = 'Ocurrió un error al enviar el requerimiento'

    if (error?.message === 'Network Error') {
      errorMessage = 'Error de conexión. Verifique su conexión a internet y vuelva a intentarlo'
    }

    if (data?.files && Array.isArray(data.files)) {
      const hasEmptyFiles = data.files.some((file: any) => !file || file.size === 0 || Object.keys(file).length === 0)
      if (hasEmptyFiles) {
        errorMessage = 'Uno o más archivos están vacíos o dañados. Por favor, vuelva a cargarlos e intente nuevamente'
      }
    }

    this.loadNotification({
      message: errorMessage,
      notificationType: 'error',
    })
  }

  private validateFormBeforeSubmit (): boolean {
    if (!(this.$refs.form as any).validate()) {
      this.loadNotification({
        message: 'Por favor complete todos los campos requeridos',
        notificationType: 'error',
      })
      return false
    }

    // Validación adicional de archivos antes de enviar
    if (!this.formData.files.length) {
      this.loadNotification({
        message: 'Debe adjuntar al menos un archivo',
        notificationType: 'error',
      })
      return false
    }

    // Verificar que los archivos no estén vacíos o corruptos
    const hasEmptyFiles = this.formData.files.some(file => !file.name || file.size === 0)
    if (hasEmptyFiles) {
      this.loadNotification({
        message: 'Uno o más archivos están vacíos o corruptos. Por favor, vuelva a cargarlos',
        notificationType: 'error',
      })
      return false
    }

    return true
  }

  async handleSubmit () {
    if (!this.validateFormBeforeSubmit()) {
      return
    }

    const fullUrl = window.location.pathname
    let data: any = null

    await updateJWT()
    try {
      this.loading = true
      const { formData, preparedData } = this.prepareFormData(fullUrl)
      data = preparedData
      const token = localStorage.getItem('apollo-token')

      const { data: { task_id: taskId } } = await axios.post(`${VUE_APP_BASE_URL}/clickup-integration/create-ticket`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
        timeout: 30000, // Añadir un timeout de 30 segundos
      })

      this.currentTaskId = taskId
      this.showTaskIdDialog = true
      this.resetForm() // Resetear el formulario después de un envío exitoso

      await this.loadNotification({
        message: `${this.formData.type} enviado correctamente`,
        notificationType: 'success',
      })
    } catch (error) {
      this.handleError(error, data)
    } finally {
      this.loading = false
      this.dialog = false
    }
  }

  private copyTaskId () {
    navigator.clipboard.writeText(this.currentTaskId)
    this.loadNotification({
      message: 'ID copiado al portapapeles',
      notificationType: 'success',
    })
  }

  async openDialog () {
    this.dialog = true
  }

  private openFileInput () {
    const fileInput = document.createElement('input')
    fileInput.type = 'file'
    fileInput.multiple = true
    fileInput.accept = this.accept
    fileInput.onchange = (e: Event) => {
      const target = e.target as HTMLInputElement
      if (target.files) {
        const files = Array.from(target.files)
        // Verificar archivos antes de asignarlos
        const hasEmptyFiles = files.some(file => file.size === 0)
        if (hasEmptyFiles) {
          this.loadNotification({
            message: 'Se detectaron archivos vacíos. Por favor, seleccione archivos válidos',
            notificationType: 'warning',
          })
          return
        }
        this.formData.files = files
      }
    }
    fileInput.click()
  }
  }
